/*============================================================
   REACT DEPENDENCIES
============================================================*/
import { useState, useEffect } from "react";

/*============================================================
   REDUX DEPENDENCIES
============================================================*/
import { useDispatch, useSelector } from "react-redux";

import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid2";
import TextareaAutosize from "@mui/material/TextareaAutosize";
import Typography from "@mui/material/Typography";

import GridSectionHeader from "../../widgets/GridSectionHeader";
import GridTextfield from "../../widgets/GridTextfield";

import {
  valudateTextfield2,
  validateCurrencyField2,
  validateCurrencyField3,
  removeCommaFromCurrency,
  convertLocaleString,
} from "../../../utils/fieldValidator.js";

import { updateChildrenEducation } from "../../../states/slcObjectives.js";

import { calculateInflatedFutureValue } from "../../../utils/formula.js";

export default function ChildrenEducation() {
  const childEdu = useSelector((state) => state.objectives.wealthaccumulation.childrenEducation);

  const [nameOfChild, setNameOfChild] = useState(childEdu.nameOfChild);
  const [nameOfChildError, setNameOfChildError] = useState(childEdu.nameOfChildError);
  const [yearToUni, setYearToUni] = useState(childEdu.yearToUni);
  const [annEduCost, setAnnEduCost] = useState(childEdu.annEduCost);
  const [pctInfRate, setPctInfRate] = useState(childEdu.pctInfRate);
  const [courseDuration, setCourseDuration] = useState(childEdu.courseDuration);
  const [fundsRequired, setFundsRequired] = useState(childEdu.fundsRequired);

  const [resLifeInsCashValueRoi, setResLifeInsCashValueRoi] = useState(childEdu.resLifeInsCashValueRoi);
  const [resLifeInsCashValue, setResLifeInsCashValue] = useState(childEdu.resLifeInsCashValue);
  const [resCashAssetsRoi, setResCashAssetsRoi] = useState(childEdu.resCashAssetsRoi);
  const [resCashAssets, setResCashAssets] = useState(childEdu.resCashAssets);
  const [resInvestmentsRoi, setResInvestmentsRoi] = useState(childEdu.resInvestmentsRoi);
  const [resInvestments, setResInvestments] = useState(childEdu.resInvestments);
  const [resOtherDesc, setResOtherDesc] = useState(childEdu.resOtherDesc);
  const [resOtherDescRoi, setResOtherDescRoi] = useState(childEdu.resOtherDescRoi);
  const [resOtherDescAmt, setResOtherDescAmt] = useState(childEdu.resOtherDescAmt);
  const [resOtherDescError, setResOtherDescError] = useState(childEdu.resOtherDescError);

  const [fundAvailAtRetireAge, setFundAvailAtRetireAge] = useState(childEdu.fundAvailAtRetireAge);
  const [surplusShortfall, setSurplusShortfall] = useState(childEdu.surplusShortfall);

  const [deviationReason, setDeviationReason] = useState(childEdu.deviationReason);
  const [deviationReasonError, setDeviationReasonError] = useState();

  const dispatch = useDispatch();

  //COMPONENT ID
  let id = "";

  /**==============================
   * EFFECTS
   ==============================*/
  useEffect(calcFundsRequired, [yearToUni, annEduCost, pctInfRate, courseDuration]);
  useEffect(calcFundsAvailAtRetirementAge, [resLifeInsCashValue, resCashAssets, resInvestments, resOtherDescAmt]);
  useEffect(calcSurplusOrShortfall, [fundAvailAtRetireAge, fundsRequired]);

  /**==============================
   * FUNCTIONS
   ==============================*/
  function calcFundsRequired() {
    //CALCULATE FV (Desired Monthly Income)
    let futureValue = calculateInflatedFutureValue(
      Number(removeCommaFromCurrency(annEduCost)) * courseDuration,
      Number(pctInfRate),
      Number(yearToUni)
    );

    //CONVERT INTO COMMA CURRENCY STRING WITH COMMA
    futureValue = convertLocaleString(futureValue, "en-US", 2);

    updateRedux("fundsRequired", futureValue, updateChildrenEducation);
    setFundsRequired(futureValue);
  }

  function calcFundsAvailAtRetirementAge() {
    let totalFundAtRetirementAge =
      Number(removeCommaFromCurrency(resLifeInsCashValue)) +
      Number(removeCommaFromCurrency(resCashAssets)) +
      Number(removeCommaFromCurrency(resInvestments)) +
      Number(removeCommaFromCurrency(resOtherDescAmt));

    totalFundAtRetirementAge = convertLocaleString(totalFundAtRetirementAge, "en-US", 2);
    updateRedux("fundAvailAtRetireAge", totalFundAtRetirementAge, updateChildrenEducation);
    setFundAvailAtRetireAge(totalFundAtRetirementAge);
  }

  function calcSurplusOrShortfall() {
    let surplusShortfall =
      Number(removeCommaFromCurrency(fundAvailAtRetireAge)) - Number(removeCommaFromCurrency(fundsRequired));

    surplusShortfall = convertLocaleString(surplusShortfall, "en-US", 2);

    updateRedux("surplusShortfall", surplusShortfall, updateChildrenEducation);
    setSurplusShortfall(surplusShortfall);
  }

  function updateRedux(fieldName, fieldValue, reducer) {
    const action = {
      type: fieldName,
      payload: fieldValue,
    };
    dispatch(reducer(action));
  }

  function updateRedux(fieldName, fieldValue, reducer) {
    const action = {
      type: fieldName,
      payload: fieldValue,
    };
    dispatch(reducer(action));
  }

  function handleChange(e, object) {
    let input_data = {
      event: e,
      fieldName: "",
      fieldVariable: "",
      isMandatory: false,
    };

    //TEXTFIELDS
    id = e.target.id;
    validateFields(e, id, input_data);
  }

  function validateFields(e, id, input_data) {
    /**==============================
     * EDUCATION NEEDS
     ==============================*/
    if (id === "nameOfChild") {
      input_data.fieldName = "Name Of Child";
      input_data.fieldVariable = nameOfChild;
      const result = valudateTextfield2(input_data);
      if (result.errorMessage === "") {
        setNameOfChild(result.value);
        updateRedux("nameOfChild", result.value, updateChildrenEducation);
      }

      setNameOfChildError(result.errorMessage);
    }

    if (id === "yearToUni") {
      input_data.fieldName = "Years to University";
      input_data.fieldVariable = yearToUni;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setYearToUni(result.value);
        updateRedux("yearToUni", result.value, updateChildrenEducation);
      }
    }

    if (id === "annEduCost") {
      input_data.fieldName = "Annual Education Cost";
      input_data.fieldVariable = annEduCost;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setAnnEduCost(result.value);
        updateRedux("annEduCost", result.value, updateChildrenEducation);
      }
    }

    if (id === "pctInfRate") {
      input_data.fieldName = "Inflation Rate";
      input_data.fieldVariable = pctInfRate;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setPctInfRate(result.value);
        updateRedux("pctInfRate", result.value, updateChildrenEducation);
      }
    }

    if (id === "courseDuration") {
      input_data.fieldName = "Course Duration";
      input_data.fieldVariable = courseDuration;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setCourseDuration(result.value);
        updateRedux("courseDuration", result.value, updateChildrenEducation);
      }
    }

    /**==============================
     * EXISTING RESOURCES ALLOCATED
     ==============================*/
    if (id === "resLifeInsCashValueRoi") {
      input_data.fieldName = "ROI(%)";
      input_data.fieldVariable = resLifeInsCashValueRoi;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setResLifeInsCashValueRoi(result.value);
        updateRedux("resLifeInsCashValueRoi", result.value, updateChildrenEducation);
      }
    }

    if (id === "resLifeInsCashValue") {
      input_data.fieldName = "Life Insurance Cash Value";
      input_data.fieldVariable = resLifeInsCashValue;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        console.log("B = " + result.value);
        setResLifeInsCashValue(result.value);
        updateRedux("resLifeInsCashValue", result.value, updateChildrenEducation);
      }
    }

    if (id === "resCashAssetsRoi") {
      input_data.fieldName = "ROI(%)";
      input_data.fieldVariable = resCashAssetsRoi;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setResCashAssetsRoi(result.value);
        updateRedux("resCashAssetsRoi", result.value, updateChildrenEducation);
      }
    }

    if (id === "resCashAssets") {
      input_data.fieldName = "Cash Assets";
      input_data.fieldVariable = resCashAssets;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setResCashAssets(result.value);
        updateRedux("resCashAssets", result.value, updateChildrenEducation);
      }
    }

    if (id === "resInvestmentsRoi") {
      input_data.fieldName = "ROI(%)";
      input_data.fieldVariable = resInvestmentsRoi;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setResInvestmentsRoi(result.value);
        updateRedux("resInvestmentsRoi", result.value, updateChildrenEducation);
      }
    }

    if (id === "resInvestments") {
      input_data.fieldName = "Investments";
      input_data.fieldVariable = resInvestments;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setResInvestments(result.value);
        updateRedux("resInvestments", result.value, updateChildrenEducation);
      }
    }

    if (id === "resOtherDesc") {
      input_data.fieldName = "Others description";
      input_data.fieldVariable = resOtherDesc;
      const result = valudateTextfield2(input_data);
      if (result.errorMessage === "") {
        setResOtherDesc(result.value);
        updateRedux("resOtherDesc", result.value, updateChildrenEducation);
      }

      setResOtherDescError(result.errorMessage);
    }

    if (id === "resOtherDescRoi") {
      input_data.fieldName = "ROI(%)";
      input_data.fieldVariable = resOtherDescRoi;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setResOtherDescRoi(result.value);
        updateRedux("resOtherDescRoi", result.value, updateChildrenEducation);
      }
    }

    if (id === "resOtherDescAmt") {
      input_data.fieldName = "Other Resource Amount";
      input_data.fieldVariable = resOtherDescAmt;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setResOtherDescAmt(result.value);
        updateRedux("resOtherDescAmt", result.value, updateChildrenEducation);
      }
    }

    if (id === "deviationReason") {
      input_data.fieldName = "Deviation reason";
      input_data.fieldLength = 1000;
      input_data.fieldVariable = deviationReason;
      const result = valudateTextfield2(input_data);
      if (result.errorMessage === "") {
        setDeviationReason(result.value);
        updateRedux("deviationReason", result.value, updateChildrenEducation);
      }

      setDeviationReasonError(result.errorMessage);
    }
  }

  return (
    <>
      <Grid container spacing={2} size={12}>
        <GridSectionHeader
          sectionName="Planning for Children’s Tertiary Education"
          sx={{
            p: 1,
            pl: 1.8,
            mt: 0,
            mb: 1,
            fontSize: 18,
            fontWeight: "bold",
            color: "#FFFFFF", //WHITE
            // color: "#000000", //BLACK
            // backgroundColor: "#d9d9d9",
            backgroundColor: "#10B981",
          }}
        />

        {/*==============================
          RETIREMENT AGE
        ==============================*/}
        <Grid size={2}>
          <Typography
            variant="body1"
            sx={{
              pt: 2.0,
            }}
          >
            Name of Child
          </Typography>
        </Grid>

        <GridTextfield
          id="nameOfChild"
          key="nameOfChild"
          columnSize="10"
          currState={nameOfChild}
          onChange={handleChange}
          error={nameOfChildError}
          helperText={nameOfChildError}
        />

        {/*==============================
          NUMBER OF YEARS IN UNIVERSITY
        ==============================*/}
        <Grid size={9}>
          <Typography
            variant="body1"
            sx={{
              pt: 2.0,
            }}
          >
            Number of Years to University (n)
          </Typography>
        </Grid>

        <GridTextfield
          id="yearToUni"
          key="yearToUni"
          columnSize="3"
          currState={yearToUni}
          onChange={handleChange}
          disabled={false}
        />

        {/*==============================
          CURRENT ANNUAL COST OF TERTIARY EDUCATION
        ==============================*/}
        <Grid size={9}>
          <Typography
            variant="body1"
            sx={{
              pt: 2.0,
            }}
          >
            Current Annual Cost of Tertiary Education (Tuition + Allowances) (pv)
          </Typography>
        </Grid>
        <GridTextfield
          id="annEduCost"
          key="annEduCost"
          columnSize="3"
          label="Annually"
          isCurrency={true}
          currState={annEduCost}
          onChange={handleChange}
        />

        {/*==============================
          CURRENT ANNUAL COST OF TERTIARY EDUCATION
        ==============================*/}
        <Grid size={9}>
          <Typography
            variant="body1"
            sx={{
              pt: 2.0,
            }}
          >
            Education Inflation Rate (%) (i)
          </Typography>
          <Typography variant="body1">(Input between 0% - 9%. If deviate, Representative to justify)</Typography>
        </Grid>

        <GridTextfield
          id="pctInfRate"
          key="pctInfRate"
          columnSize="3"
          label="Percent"
          currState={pctInfRate}
          onChange={handleChange}
        />

        {/*==============================
          CURRENT ANNUAL COST OF TERTIARY EDUCATION
        ==============================*/}
        <Grid size={9}>
          <Typography
            variant="body1"
            sx={{
              pt: 2.0,
            }}
          >
            Course Duration (years)
          </Typography>
        </Grid>

        <GridTextfield
          id="courseDuration"
          key="courseDuration"
          columnSize="3"
          currState={courseDuration}
          onChange={handleChange}
        />

        {/*==============================
          FUNDS REQUIRED
        ==============================*/}
        <Grid size={9}>
          <Typography
            variant="body1"
            sx={{
              pt: 2.0,
              fontWeight: "bold",
            }}
          >
            (A) Funds Required(fv)
          </Typography>
        </Grid>

        <GridTextfield
          id="fundsRequired"
          key="fundsRequired"
          columnSize="3"
          isCurrency={true}
          currState={fundsRequired}
          onChange={handleChange}
          disabled={true}
        />
      </Grid>

      {/*==============================
          FUNDS REQUIRED
       ==============================*/}
      <Grid size={12}>
        <Typography
          variant="h5"
          sx={{
            pt: 2.0,
            fontWeight: "bold",
            textDecoration: "underline",
          }}
        >
          Existing Resources Allocated for Children’s Education
        </Typography>
      </Grid>

      {/*==============================
        LIFE INSURANCE CASH VALUES
      ==============================*/}
      <Grid size={6}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.0,
          }}
        >
          Life insurance Cash Value
        </Typography>
      </Grid>

      <GridTextfield
        id="resLifeInsCashValueRoi"
        key="resLifeInsCashValueRoi"
        columnSize="3"
        label="ROI (%)"
        currState={resLifeInsCashValueRoi}
        onChange={handleChange}
        disabled={false}
      />

      <GridTextfield
        id="resLifeInsCashValue"
        key="resLifeInsCashValue"
        columnSize="3"
        isCurrency={true}
        currState={resLifeInsCashValue}
        onChange={handleChange}
        disabled={false}
      />

      {/*==============================
        CASH ASSET
      ==============================*/}
      <Grid size={6}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.0,
          }}
        >
          Cash Assets (Savings, Fixed Deposits, etc.) #
        </Typography>
      </Grid>

      <GridTextfield
        id="resCashAssetsRoi"
        key="resCashAssetsRoi"
        columnSize="3"
        label="ROI (%)"
        currState={resCashAssetsRoi}
        onChange={handleChange}
        disabled={false}
      />

      <GridTextfield
        id="resCashAssets"
        key="resCashAssets"
        columnSize="3"
        isCurrency={true}
        currState={resCashAssets}
        onChange={handleChange}
        disabled={false}
      />

      {/*==============================
        INVESTMENTS (BONDS, UNIT TRUSTS, SHARES, ETC...)
      ==============================*/}
      <Grid size={6}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.0,
          }}
        >
          Investments (Bonds, Unit Trusts, Shares, etc.) #
        </Typography>
      </Grid>

      <GridTextfield
        id="resInvestmentsRoi"
        key="resInvestmentsRoi"
        columnSize="3"
        label="ROI (%)"
        currState={resInvestmentsRoi}
        onChange={handleChange}
        disabled={false}
      />

      <GridTextfield
        id="resInvestments"
        key="resInvestments"
        columnSize="3"
        isCurrency={true}
        currState={resInvestments}
        onChange={handleChange}
        disabled={false}
      />

      {/*==============================
        OTHERS
      ==============================*/}
      <Grid size={1}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.0,
          }}
        >
          Others:
        </Typography>
      </Grid>

      <GridTextfield
        id="resOtherDesc"
        key="resOtherDesc"
        columnSize="5"
        currState={resOtherDesc}
        onChange={handleChange}
        error={resOtherDescError}
        helperText={resOtherDescError}
      />

      <GridTextfield
        id="resOtherDescRoi"
        key="resOtherDescRoi"
        columnSize="3"
        label="ROI (%)"
        currState={resOtherDescRoi}
        onChange={handleChange}
        disabled={false}
      />

      <GridTextfield
        id="resOtherDescAmt"
        key="resOtherDescAmt"
        columnSize="3"
        isCurrency={true}
        currState={resOtherDescAmt}
        onChange={handleChange}
        disabled={false}
      />

      {/*==============================
          FUNDS AVAILABLE
        ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.0,
            fontWeight: "bold",
          }}
        >
          (B) Funds Avallable At Retirement Age
        </Typography>
      </Grid>

      <GridTextfield
        id="fundAvailAtRetireAge"
        key="fundAvailAtRetireAge"
        columnSize="3"
        isCurrency={true}
        currState={fundAvailAtRetireAge}
        onChange={handleChange}
        disabled={true}
      />

      {/*==============================
        SURPLUS / SHORTFALL
      ==============================*/}
      <Grid size={9}>
        <Typography
          variant="body1"
          sx={{
            pt: 2.0,
            fontWeight: "bold",
          }}
        >
          (B) - (A) = Surplus / (Shortfall)
        </Typography>
      </Grid>

      <GridTextfield
        id="surplusShortfall"
        key="surplusShortfall"
        columnSize="3"
        isCurrency={true}
        currState={surplusShortfall}
        onChange={handleChange}
        disabled={true}
      />

      <Typography variant="body1">Reason(s) for Deviations:</Typography>
      <TextareaAutosize
        id="deviationReason"
        placeholder="Type here..."
        style={{ width: "100%" }}
        minRows={5}
        onChange={handleChange}
        defaultValue={deviationReason}
      />
      {deviationReasonError && <FormHelperText error={deviationReasonError}>{deviationReasonError}</FormHelperText>}

      <Typography variant="body2">
        # Summation of Cash Assets allocated in needs analysis for retirement, children’s education and/or savings for
        other purposes should not exceed 100% of Total Cash/Near Cash declared in Section 6. If deviate, Representative
        to justify.
      </Typography>

      <Typography variant="body2">
        Similarly for Investments, summation of Investments allocated in needs analysis for retirement, children’s
        education and/or savings for other purposes should not exceed 100% of Unit Trust + Stocks + Bonds declared in
        Section 6. If deviate, Representative to justify.
      </Typography>
    </>
  );
}
